// React Dependencies
import React, { createRef } from 'react';

// External Dependencies
import { Map, Polygon, GoogleApiWrapper, Marker, InfoWindow, Polyline, Circle } from 'google-maps-react';
import Button from '@material-ui/core/Button';
import _ from 'lodash';
import PropTypes from 'prop-types';

// Internal Dependencies
import Constants from '../../common/constants';
import { computeOffset } from '../../common/util';
import { IsEmpty, IsValidHexColor } from '../../common/checks';
import Images from '../basics/Images';

const inside = require('point-in-polygon');

const GoogleKey = process.env.REACT_APP_GOOGLE_API_KEY;

const defaultCoord = {
  lat: 1.27978,
  lng: 103.864868,
};
const SINGAPORE_MIN_ZOOM = 11;

class AreaMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // coords: [defaultCoord, { lat: defaultCoord.lat + 0.000004, lng: defaultCoord.lng + 0.00000005 }],
      coords: [],
      zoom:   14,
      center: {
        lat: 1.27978,
        lng: 103.864868,
      },
      calloutCoord:      {},
      activeMarker:      {},
      showingInfoWindow: false,
      mapLoaded:         false,
    };
    this.mapRef = createRef();
    this.onMapClick = this.onMapClick.bind(this);
    this.onMarkerDragEnd = this.onMarkerDragEnd.bind(this);
    this.onMarkerDrag = this.onMarkerDrag.bind(this);
    this.onMapRightClick = this.onMapRightClick.bind(this);
    this.onZoomInClicked = this.onZoomInClicked.bind(this);
    this.onZoomOutClicked = this.onZoomOutClicked.bind(this);
    this.removeLastPointClicked = this.removeLastPointClicked.bind(this);
    this.testMarkerInPolygonClicked = this.testMarkerInPolygonClicked.bind(this);
    this.createMapSquare = this.createMapSquare.bind(this);
  }

  componentDidMount() {
    if (this.props.mapCoordinates.length > 0) {
      this.centraliseMapView(this.props.mapCoordinates);
      this.setState({ coords: this.props.mapCoordinates });
    }

    if (this.props.isCalloutCoord) {
      if (this.props.mapCoordinates.length === 0
        && !IsEmpty(this.props.calloutCoord.lat)
        && !IsEmpty(this.props.calloutCoord.lng)
      ) {
        this.centraliseMapView([this.props.calloutCoord]);
      }
      this.setState({ calloutCoord: this.props.calloutCoord });
    }
    this.setState({ zoom: this.props.zoom });
  }

  componentDidUpdate(prevProps, prevState) {
    this.changeCalloutColour(this.props.calloutColour);

    if (this.state.calloutCoord !== prevState.calloutCoord) {
      this.setState({
        activeMarker:      { position: this.state.calloutCoord },
        showingInfoWindow: true,
      });
    }

    if (this.props.mapCoordinates !== prevProps.mapCoordinates) {
      this.setState(
        { coords: this.props.mapCoordinates },
        () => this.centraliseMapView(this.props.mapCoordinates),
      );
    }

    if (
      (this.props.calloutCoord.lat !== prevProps.calloutCoord.lat)
      || (this.props.calloutCoord.lng !== prevProps.calloutCoord.lng)
    ) {
      this.setState(
        { calloutCoord: this.props.calloutCoord },
        () => {
          if (
            this.props.mapCoordinates.length === 0
            && !IsEmpty(this.props.calloutCoord.lat)
            && !IsEmpty(this.props.calloutCoord.lng)
          ) {
            this.centraliseMapView([this.props.calloutCoord]);
          }
        },
      );
    }

    if (this.props.zoom !== prevProps.zoom) {
      this.setState({ zoom: this.props.zoom });
    }
  }

  centraliseMapView(data) {
    const arrLat = _.sortBy(_.map(data, (c) => c.lat));
    const arrLng = _.sortBy(_.map(data, (c) => c.lng));
    const highestLat = arrLat[arrLat.length - 1];
    const highestLng = arrLng[arrLng.length - 1];
    console.log(highestLat, highestLng);
    this.setState({
      center: {
        lat: highestLat,
        lng: highestLng,
      },
    });
  }

  autofitMapView(map, maps) {
    const points = [];

    this.state.coords.forEach((c) => points.push(c));

    if (!IsEmpty(this.props.calloutCoord.lat) && !IsEmpty(this.props.calloutCoord.lng)) {
      points.push(this.props.calloutCoord);
    }

    this.props.additionalMarkers.forEach((m) => {
      const lat = parseFloat(m.lat);
      const lng = parseFloat(m.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        points.push({ lat, lng });
      }
    });

    if (IsEmpty(points)) {
      return;
    }

    const bounds = new maps.LatLngBounds();
    points.forEach((p) => bounds.extend(new maps.LatLng(p.lat, p.lng)));

    const center = bounds.getCenter();
    this.setState({
      center: { lat: center.lat(), lng: center.lng() },
    });

    map.fitBounds(bounds);

    // fitBounds triggers an async zoom change; clamp it once idle
    maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() < SINGAPORE_MIN_ZOOM) {
        map.setZoom(SINGAPORE_MIN_ZOOM);
      }
      this.setState({ zoom: map.getZoom() });
    });
  }

  changeCalloutColour = (colour) => {
    if (IsEmpty(colour) || !IsValidHexColor(colour.slice(1))) {
      return;
    }
    const infoWindowContainer = document.querySelector('.gm-style-iw-c');
    if (infoWindowContainer) {
      infoWindowContainer.style.backgroundColor = colour;
    }
  }

  createMapSquare(location, meter) {
    const topLeft       = computeOffset(location, meter, 135);
    const topRight      = computeOffset(location, meter, 45);
    const bottomLeft    = computeOffset(location, meter, 225);
    const bottomRight   = computeOffset(location, meter, 315);
    const squareCoords  = [topLeft, topRight, bottomRight, bottomLeft];

    return squareCoords;
  }

  onMapClick(mapProps, map, evt) {
    if (this.props.disabled) {
      return;
    }
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();

    if (!this.props.isCalloutCoord) {
      let { coords } = this.state;

      if (this.props.areaType === Constants.AreaType.MRT) {
        const squareCoords = this.createMapSquare({ lat, lng }, 750);
        coords = [];
        coords.push(...squareCoords);
      } else {
        coords.push({ lat, lng });
      }

      this.setState({ coords }, () => {
        this.props.setMapCoor(this.state.coords);
      });
    } else {
      this.setState({
        calloutCoord:      { lat, lng },
        activeMarker:      { position: { lat, lng } },
        showingInfoWindow: true,
      });
      this.props.setCalloutCoord({ lat, lng });
    }
  }

  onMarkerDragEnd(mapProps, map, evt) {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();

    this.setState({
      calloutCoord:      { lat, lng },
      activeMarker:      { position: { lat, lng } },
      showingInfoWindow: true,
    });

    this.props.setCalloutCoord({ lat, lng });
  }

  onMarkerDrag(mapProps, map, evt) {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();

    this.setState({
      calloutCoord:      { lat, lng },
      activeMarker:      { position: { lat, lng } },
      showingInfoWindow: true,
    });
  }

  onMapRightClick(mapProps, map, evt) {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const { coords } = this.state;
    coords.push({ lat, lng });
    this.setState({ coords });
  }

  onZoomInClicked() {
    this.setState((prevState) => ({ zoom: prevState.zoom + 1 }));
  }

  onZoomOutClicked() {
    this.setState((prevState) => ({ zoom: prevState.zoom - 1 }));
  }

  removeLastPointClicked() {
    let { coords } = this.state;

    if (coords && coords.length > 0) {
      if (this.props.areaType === Constants.AreaType.MRT) {
        coords = [];
      } else {
        coords.splice(coords.length - 1, 1);
      }

      this.setState({ coords }, () => {
        this.props.setMapCoor(this.state.coords);
      });
    }
  }

  testMarkerInPolygonClicked(lat, lng) {
    const polygonPts = _.map(this.state.coords, (c) => [c.lat, c.lng]);
    const isInside = inside([lat, lng], polygonPts);
    console.log('IsInside=', isInside);
  }

  onMarkerClick = (props, marker) => {
    this.setState({
      activeMarker:      marker,
      showingInfoWindow: true,
    });
  }

  onMarkerMounted = (element) => {
    if (!IsEmpty(element)) {
      this.onMarkerClick(element.props, element.marker);
    }
  };

  handleMapIdle = () => {
    this.setState({ mapLoaded: true });
  };

  render() {
    const { Heading } = this.props;
    const coordsPolygonColour = IsEmpty(this.props.colour) ? this.props.defaultColour : this.props.colour;

    return (
      <div className="container-fluid m-0 p-0" style={this.props.style}>
        <div style={this.props.buttonContainerStyles}>
          { Heading && <Heading />}
          { !this.props.isCalloutCoord && (
            <div style={{ display: 'block', marginBottom: '13px', ...this.props.buttonSubContainerStyle }}>
              <Button variant="contained" size="large" className="mr-2" color="secondary" onClick={this.onZoomInClicked}>+ Zoom In</Button>
              <Button variant="contained" size="large" className="mr-2" color="secondary" onClick={this.onZoomOutClicked}>- Zoom Out</Button>
              {this.state.coords.length > 0 && (
                <Button disabled={this.props.disabled} variant="contained" size="large" color="secondary" onClick={this.removeLastPointClicked}>
                  { this.props.areaType === Constants.AreaType.MRT ? 'Remove Square Points' : 'Remove Last Point' }
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', width: '100%', height: this.props.mapHeight }}>
          <Map
            ref={(r) => { this.mapRef = r; }}
            google={this.props.google}
            zoom={this.state.zoom}
            zoomControl={false}
            style={{ position: 'absolute', cursor: 'default', top: 0, left: 0, width: '100%', maxWidth: '100%', height: 'auto' }}
            initialCenter={defaultCoord}
            center={this.state.center}
            onClick={this.onMapClick}
            //scrollwheel={false}
            // onRightclick={this.onMapRightClick}
            onIdle={this.handleMapIdle}
            onReady={(mapProps, map) => {
              map.setOptions({
                draggableCursor: 'default',
                draggingCursor:  'pointer',
              });

              if (this.props.autofitOnLoad) {
                this.autofitMapView(map, this.props.google.maps);
              }
            }}
          >

            {this.state.mapLoaded
              && !IsEmpty(this.props.additionalMarkers)
              && this.props.additionalMarkers.map((m) => {
                const lat = parseFloat(m.lat);
                const lng = parseFloat(m.lng);

                // Skip if coordinates are invalid
                if (isNaN(lat) || isNaN(lng)) {
                  return null;
                }

                return (
                  // "%23 here's on Icon url used as url encoding of #"
                  <Marker
                    position={{ lat, lng }}
                    icon={{
                      url:        Images.Local.PoiPhotoLocationMarker,
                      scaledSize: new window.google.maps.Size(20, 20),
                    }}
                  />
                );
              })}

            {this.props.isCalloutCoord && this.state.mapLoaded && (
              <Marker
                ref={this.onMarkerMounted}
                position={this.state.calloutCoord}
                onClick={this.onMarkerClick}
                draggable={!this.props.disabled}
                onDragend={this.onMarkerDragEnd}
                onDrag={this.onMarkerDrag}
                icon={
                  this.props.isPoiMarker
                    ? {
                      url:        this.props.markerIcon,
                      scaledSize: new window.google.maps.Size(this.props.markerScaledSize, this.props.markerScaledSize),
                    }
                    : undefined
                }
              />
            )}

            {this.props.isCalloutCoord && this.props.showCalloutInfoWindow && (
              <InfoWindow
                position={{
                  lat: this.state.calloutCoord.lat + 0.00320, // adjusts the offset value to ensure the InfoWindow does not cover the Marker.
                  lng: this.state.calloutCoord.lng,
                }}
                marker={this.state.activeMarker}
                visible={this.state.showingInfoWindow}
                onClose={() => this.setState({ showingInfoWindow: false })}
              >
                <div className="text-center">
                  <p style={{ fontSize: 12, marginBottom: 5, fontWeight: 'bold', whiteSpace: 'pre-line' }}>{this.props.title}</p>
                  <p style={{ fontSize: 10, whiteSpace: 'pre-line' }}>{this.props.content}</p>
                </div>
              </InfoWindow>
            )}

            <Polyline
              key={_.uniqueId()} // "unique key" needed, in order to update the line
              path={this.state.coords}
              strokeColor={coordsPolygonColour}
            />

            {
              this.state.coords.length > 1 && (
                <Polyline
                  key={_.uniqueId()} // "unique key" needed, in order to update the line
                  path={[this.state.coords[0], this.state.coords[this.state.coords.length - 1]]}
                  strokeColor={coordsPolygonColour}

                />
              )
            }

            {
              // to indicate the start point
              !IsEmpty(this.state.coords) && (
                <Circle
                  center={this.state.coords[0]}
                  radius={1}
                  strokeColor="black"
                  strokeOpacity={1}
                />
              )
            }
            {
              // To indicate the last point
              this.state.coords.length > 1 && (
                <Circle
                  center={this.state.coords[this.state.coords.length - 1]}
                  radius={1}
                  strokeColor="red"
                  strokeOpacity={1}
                />
              )
            }

            {
              _.map(this.props.districtsCoords, (coord, index) => (
                <Polygon
                  key={`${coord.length}-${index}`}
                  paths={coord}
                  strokeColor="#3A1078"
                  strokeOpacity={0.30}
                  strokeWeight={2}
                  fillColor="#3A1078"
                  fillOpacity={0.30}
                />
              ))
            }
          </Map>
        </div>

        { /** UI To test point-in-polygon */}
        {/* <div style={{ position: 'relative', width: '100%', height: 500 }}>
          <Map
            google={this.props.google}
            zoom={this.state.zoom}
            zoomControl={false}
            style={{ position: 'absolute', cursor: 'default', top: 0, left: 0, width: '100%', maxWidth: '100%', height: 'auto' }}
            initialCenter={defaultCoord}
            center={this.state.center}
            onClick={(mapProps, map, evt) => {
              const lat = evt.latLng.lat();
              const lng = evt.latLng.lng();
            this.testMarkerInPolygonClicked(lat, lng);
            }}
          >
            <Polyline
              key={_.uniqueId()} // "unique key" needed, in order to update the line
              path={this.state.coords}
              strokeColor={coordsPolygonColour}
            />
            {
              this.state.coords.length > 1 && (
                <Polyline
                  key={_.uniqueId()} // "unique key" needed, in order to update the line
                  path={[this.state.coords[0], this.state.coords[this.state.coords.length - 1]]}
                  strokeColor={coordsPolygonColour}
                />
              )
            }
          </Map>
        </div> */}

        {/* <MyMapComponent
            isMarkerShown
            // googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${GoogleKey}&v=3.exp&libraries=geometry,drawing,places`}
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: `80vh` }} />}
            mapElement={<div style={{ height: `100%` }} />}
          /> */}
        {/* <div style={{ position: 'fixed', top: 0, left: 0, right: 0, flex: 1, zIndex: 1, backgroundColor: '#ff000033', width: '100%', height: '80vh' }} /> */}
      </div>
    );
  }
}

export default GoogleApiWrapper({
  apiKey: `${GoogleKey}`,
})(AreaMap);

AreaMap.propTypes = {
  disabled:                PropTypes.bool,
  style:                   PropTypes.shape(Object),
  buttonContainerStyles:   PropTypes.shape(Object),
  buttonSubContainerStyle: PropTypes.shape(Object),
  colour:                  PropTypes.string,
  defaultColour:           PropTypes.string,
  areaType:                PropTypes.string,
  districtsCoords:         PropTypes.arrayOf(PropTypes.array),
  google:                  PropTypes.objectOf(PropTypes.object),
  mapCoordinates:          PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  })),
  Heading:    PropTypes.func,
  setMapCoor: PropTypes.func,
  markerIcon: PropTypes.string,

  mapHeight: PropTypes.number,

  calloutColour:         PropTypes.string,
  isCalloutCoord:        PropTypes.bool,
  setCalloutCoord:       PropTypes.func,
  showCalloutInfoWindow: PropTypes.bool,
  calloutCoord:          PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  title:             PropTypes.string,
  content:           PropTypes.string,
  zoom:              PropTypes.number,
  additionalMarkers: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  isPoiMarker:      PropTypes.bool,
  markerScaledSize: PropTypes.number,
  autofitOnLoad:    PropTypes.bool,
};

AreaMap.defaultProps = {
  disabled:                false,
  areaType:                '',
  style:                   {},
  buttonContainerStyles:   {},
  buttonSubContainerStyle: {},
  colour:                  '#f00',
  defaultColour:           '#f00',
  districtsCoords:         [],
  google:                  {},
  mapCoordinates:          [],
  mapHeight:               500,
  markerIcon:              Images.Local.MarkerPoi,
  Heading:                 null,
  calloutColour:           '#F45692',
  showCalloutInfoWindow:   true,
  additionalMarkers:       [],
  isPoiMarker:             false,
  markerScaledSize:        40,
  setMapCoor:              () => {},
  autofitOnLoad:           false,

  isCalloutCoord:  false,
  setCalloutCoord: () => {},
  calloutCoord:    {},
  title:           '',
  content:         '',
  zoom:            14,
};
